import { AbstractTranscriptEditorCommand } from './AbstractTranscriptEditorCommand';
import { TranscriptEditorCore } from '../TranscriptEditorCore';
import { TranscriptEditorChangeType } from '../types';
import { ITextElement } from '../models/interfaces';

/**
 * Command to replace selected text elements with a single text element
 * 
 * This command takes selected text elements, removes them, and replaces them
 * with a single text element that has the timing of the first and last elements.
 */
export class ReplaceSelectedTextCommand extends AbstractTranscriptEditorCommand {
  /** New text to replace the selection with */
  private readonly newText: string;
  
  /** Array of selected elements to replace */
  private readonly selectedElements: Array<{
    id: string;
    segmentId: string;
    paragraphId: string;
    type: string;
  }>;
  
  /** Original elements for undo */
  private originalElements: Array<{
    id: string;
    segmentId: string;
    paragraphId: string;
    value: string;
    startTime?: number;
    endTime?: number;
    type: string;
  }> = [];
  
  /** New element ID created by this command */
  private newElementId: string | null = null;
  
  /** ID of the user performing the operation */
  private readonly userId: string;

  /**
   * Create a new ReplaceSelectedTextCommand
   * 
   * @param editor - The transcript editor instance
   * @param selectedElements - Array of selected elements to replace
   * @param newText - The text to replace the selection with
   * @param userId - The ID of the user performing the operation
   */
  constructor(
    editor: TranscriptEditorCore,
    selectedElements: Array<{
      id: string;
      segmentId: string;
      paragraphId: string;
      type: string;
    }>,
    newText: string,
    userId: string
  ) {
    super(`Replace selected text with "${newText}"`, editor);
    
    this.selectedElements = selectedElements;
    this.newText = newText;
    this.userId = userId;
    
    // Store metadata for debugging and tracking
    this.setMetadata('newText', newText);
    this.setMetadata('userId', userId);
    this.setMetadata('selectedElementCount', selectedElements.length);
  }

  /**
   * Executes the command to replace selected text
   * 
   * @returns Promise resolving to true if successful
   */
  public async execute(): Promise<boolean> {
    try {
      if (this.selectedElements.length === 0) {
        return false;
      }

      // Start batch operation for better performance
      this.editor.beginBatch();

      // Store original elements for undo
      this.originalElements = [];
      
      // Get container
      const container = this.editor.getContainer();
      if (!container) {
        return false;
      }

      // Sort elements by their DOM position to preserve order
      const sortedElements = [...this.selectedElements];
      
      // Get data for first and last elements
      const firstElement = sortedElements[0];
      const lastElement = sortedElements[sortedElements.length - 1];
      
      // Only proceed if elements are in the same paragraph
      if (firstElement.segmentId !== lastElement.segmentId || 
          firstElement.paragraphId !== lastElement.paragraphId) {
        return false;
      }
      
      // Get timing information from the first and last elements
      let startTime: number | undefined;
      let endTime: number | undefined;
      
      // Store original elements for undo and get timing information
      for (const element of sortedElements) {
        const segment = container.getSegment(element.segmentId);
        if (!segment) continue;
        
        const paragraph = segment.getParagraph(element.paragraphId);
        if (!paragraph) continue;
        
        const el = paragraph.getElement(element.id);
        if (!el || el.type !== 'text') continue;
        
        // Store original element data
        this.originalElements.push({
          id: element.id,
          segmentId: element.segmentId,
          paragraphId: element.paragraphId,
          value: el.value,
          startTime: el.startTime,
          endTime: el.endTime,
          type: el.type
        });
        
        // Get timing from first element
        if (element.id === firstElement.id) {
          startTime = el.startTime;
        }
        
        // Get timing from last element
        if (element.id === lastElement.id) {
          endTime = el.endTime;
        }
      }
      
      // Store metadata about original elements
      this.setMetadata('originalElements', this.originalElements);
      
      // Create a new element with the combined timing
      const segmentId = firstElement.segmentId;
      const paragraphId = firstElement.paragraphId;
      
      // Delete all original elements except the first one
      for (let i = 1; i < sortedElements.length; i++) {
        const element = sortedElements[i];
        this.editor.deleteElement(element.segmentId, element.paragraphId, element.id, 'system');
      }
      
      // Update the first element with the new text and timing
      this.editor.updateElementText(
        segmentId,
        paragraphId,
        firstElement.id,
        this.newText,
        this.userId
      );
      
      // If we have timing information, update it at the paragraph level
      if (startTime !== undefined || endTime !== undefined) {
        // Get the paragraph and update the element timing manually
        const container = this.editor.getContainer();
        if (container) {
          const segment = container.getSegment(segmentId);
          if (segment) {
            const paragraph = segment.getParagraph(paragraphId);
            if (paragraph) {
              const element = paragraph.getElement(firstElement.id) as ITextElement;
              if (element && element.type === 'text') {
                if (startTime !== undefined) element.startTime = startTime;
                if (endTime !== undefined) element.endTime = endTime;
                
                // Increment element counter
                element.incrementCounter();
                
                // Force MobX reactivity by incrementing counters up the hierarchy
                paragraph.incrementCounter();
                segment.incrementCounter();
                container.incrementCounter();
                
                // Notify observers about the timing update
                this.editor.notifyObservers(TranscriptEditorChangeType.ELEMENT_UPDATED, {
                  segment,
                  paragraph,
                  element,
                  timingUpdated: true
                });
              }
            }
          }
        }
      }
      
      // Store the ID of the new element for reference
      this.newElementId = firstElement.id;
      
      // Increment container counter to trigger UI updates
      if (container) {
        container.incrementCounter();
      }
      
      // Notify observers about the text replacement
      this.editor.notifyObservers(TranscriptEditorChangeType.TEXT_REPLACED_SELECTION, {
        newText: this.newText,
        selectedElements: this.selectedElements,
        newElementId: this.newElementId
      });
      
      return true;
    } catch (error) {
      console.error('Error executing selected text replacement command:', error);
      
      return false;
    } finally {
      // End batch operation regardless of success or failure
      this.editor.endBatch();
    }
  }

  /**
   * Undoes the text replacement
   * 
   * @returns Promise resolving to true if successful
   */
  public async undo(): Promise<boolean> {
    
    try {
      // Start batch operation for better performance
      this.editor.beginBatch();

      // If we don't have original elements, we can't undo
      if (this.originalElements.length === 0) {
        return false;
      }
      
      // First element was updated, not deleted - so restore its original state
      const firstElement = this.originalElements[0];
      
      // Restore the first element's text and timing
      this.editor.updateElementText(
        firstElement.segmentId,
        firstElement.paragraphId,
        firstElement.id,
        firstElement.value,
        this.userId
      );
      
      // Restore timing
      // Restore the element timing
      const container = this.editor.getContainer();
      if (container) {
        const segment = container.getSegment(firstElement.segmentId);
        if (segment) {
          const paragraph = segment.getParagraph(firstElement.paragraphId);
          if (paragraph) {
            const element = paragraph.getElement(firstElement.id) as ITextElement;
            if (element && element.type === 'text') {
              element.startTime = firstElement.startTime;
              element.endTime = firstElement.endTime;
              
              // Notify observers about the timing update
              this.editor.notifyObservers(TranscriptEditorChangeType.ELEMENT_UPDATED, {
                segment,
                paragraph,
                element,
                timingUpdated: true
              });
            }
          }
        }
      }
      
      // Recreate all other original elements
      for (let i = 1; i < this.originalElements.length; i++) {
        const element = this.originalElements[i];
        
        // Create a new element with the original data
        // Create a new element with the original data
        const container = this.editor.getContainer();
        if (container) {
          const segment = container.getSegment(element.segmentId);
          if (segment) {
            const paragraph = segment.getParagraph(element.paragraphId);
            if (paragraph) {
              // Use factory to create element - import as needed
              const { TranscriptElementFactory } = require('../models/TranscriptElementFactory');
              
              // Create the new element
              const newElement = TranscriptElementFactory.createTextElement(
                `text-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
                element.value,
                element.startTime,
                element.endTime
              );
              
              // Update internal references
              newElement.updateInternalData(element.segmentId, element.paragraphId);
              
              // Add to paragraph
              paragraph.addElement(newElement);
              
              // Notify observers
              this.editor.notifyObservers(TranscriptEditorChangeType.ELEMENT_ADDED, {
                segment,
                paragraph,
                element: newElement
              });
            }
          }
        }
      }
      
      // Notify observers about the undo operation
      this.editor.notifyObservers(TranscriptEditorChangeType.TEXT_REPLACED_SELECTION, {
        isUndo: true,
        originalElements: this.originalElements
      });
      
      return true;
    } catch (error) {
      console.error('Error undoing selected text replacement command:', error);
      
      return false;
    } finally {
      // End batch operation regardless of success or failure
      this.editor.endBatch();
    }
  }
}